import { useState, useMemo } from 'react';
import { useApp } from '@/context/AppContext';

function formatDuration(minutes) {
  if (!minutes) return '—';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h}h ${m}m`;
}

function formatTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric' });
  } catch { return iso; }
}

export default function TimeCards() {
  const { timeCards, team, jobs, workCenters, empById, jobById, wcById, syncOfflineQueue, offlineQueue, showToast } = useApp();
  const [empFilter, setEmpFilter]   = useState('all');
  const [jobFilter, setJobFilter]   = useState('all');
  const [dateFilter, setDateFilter] = useState('week');

  const filtered = useMemo(() => {
    const now = new Date();
    const cutoff = dateFilter === 'today'
      ? new Date(now.toISOString().slice(0, 10)).getTime()
      : dateFilter === 'week'
      ? now.getTime() - 7 * 86400000
      : dateFilter === 'month'
      ? now.getTime() - 30 * 86400000
      : 0;

    return timeCards.filter(tc => {
      if (empFilter !== 'all' && tc.employeeId !== Number(empFilter)) return false;
      if (jobFilter !== 'all' && tc.jobId !== Number(jobFilter)) return false;
      if (cutoff && new Date(tc.punchedAt).getTime() < cutoff) return false;
      return true;
    }).sort((a, b) => (b.punchedAt || '').localeCompare(a.punchedAt || ''));
  }, [timeCards, empFilter, jobFilter, dateFilter]);

  const stats = useMemo(() => {
    const outs = filtered.filter(tc => tc.punchType === 'clock_out' && tc.durationMinutes);
    const totalMin = outs.reduce((a, tc) => a + (tc.durationMinutes || 0), 0);
    const unique = new Set(filtered.map(tc => tc.employeeId)).size;
    const anomalies = outs.filter(tc => tc.durationMinutes > 12 * 60).length;
    return { totalHrs: (totalMin / 60).toFixed(1), uniqueEmps: unique, anomalies };
  }, [filtered]);

  return (
    <div className="page fade-up">
      <div className="ph">
        <div>
          <div className="ptitle">Time Cards</div>
          <div className="psub">{stats.totalHrs}h · {stats.uniqueEmps} employee{stats.uniqueEmps !== 1 ? 's' : ''} · {filtered.length} punches</div>
        </div>
        <div className="pacts">
          {offlineQueue.length > 0 && (
            <button className="btn btn-accent btn-sm" onClick={() => syncOfflineQueue()}>
              Sync {offlineQueue.length} offline punch{offlineQueue.length > 1 ? 'es' : ''}
            </button>
          )}
          {stats.anomalies > 0 && (
            <span className="bdg" style={{ background: 'var(--amber-t)', color: 'var(--amber)' }}>
              ⚠ {stats.anomalies} anomal{stats.anomalies > 1 ? 'ies' : 'y'}
            </span>
          )}
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8 }}>
        <select
          value={empFilter}
          onChange={e => setEmpFilter(e.target.value)}
          style={{ background: 'var(--s2)', border: '1px solid var(--b2)', borderRadius: 8, padding: '7px 12px', fontSize: 13, color: 'var(--t1)', fontFamily: 'inherit', cursor: 'pointer' }}
        >
          <option value="all">All Employees</option>
          {team.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
        </select>
        <select
          value={jobFilter}
          onChange={e => setJobFilter(e.target.value)}
          style={{ background: 'var(--s2)', border: '1px solid var(--b2)', borderRadius: 8, padding: '7px 12px', fontSize: 13, color: 'var(--t1)', fontFamily: 'inherit', cursor: 'pointer' }}
        >
          <option value="all">All Jobs</option>
          {jobs.map(j => <option key={j.id} value={j.id}>{j.jobNumber}</option>)}
        </select>
        {['today','week','month','all'].map(d => (
          <button
            key={d}
            className={`btn btn-ghost btn-sm${dateFilter === d ? ' active' : ''}`}
            onClick={() => setDateFilter(d)}
            style={dateFilter === d ? { background: 'var(--cyan-t)', color: 'var(--cyan)', borderColor: 'var(--cyan)' } : {}}
          >
            {d.charAt(0).toUpperCase() + d.slice(1)}
          </button>
        ))}
      </div>

      <div className="card">
        <table className="tbl">
          <thead>
            <tr>
              <th>Employee</th>
              <th>Job</th>
              <th>Work Center</th>
              <th>Type</th>
              <th>Time</th>
              <th>Date</th>
              <th>Duration</th>
              <th>GPS</th>
              <th>Synced</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={9} style={{ textAlign: 'center', color: 'var(--t3)', padding: '30px' }}>No punches found for the selected filters.</td></tr>
            ) : filtered.map(tc => {
              const emp  = empById(tc.employeeId);
              const job  = jobById(tc.jobId);
              const wc   = wcById(tc.workCenterId);
              const isAnomal = tc.durationMinutes > 12 * 60;
              return (
                <tr key={tc.id} style={{ background: isAnomal ? 'var(--amber-t)' : undefined }}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {emp && (
                        <div className="emp-av" style={{ width: 26, height: 26, fontSize: 10, background: emp.color || 'var(--cyan)' }}>
                          {emp.initials || emp.name?.slice(0, 2)}
                        </div>
                      )}
                      <span style={{ fontWeight: 600, fontSize: 13 }}>{emp?.name || `Emp #${tc.employeeId}`}</span>
                    </div>
                  </td>
                  <td style={{ color: 'var(--t2)', fontSize: 12 }}>{job?.jobNumber || '—'}</td>
                  <td style={{ color: 'var(--t3)', fontSize: 12 }}>{wc?.name || '—'}</td>
                  <td>
                    <span
                      className="bdg"
                      style={{
                        background: tc.punchType === 'clock_in' ? 'var(--teal-t)' : 'var(--rose-t)',
                        color:      tc.punchType === 'clock_in' ? 'var(--teal)'   : 'var(--rose)',
                      }}
                    >
                      {tc.punchType === 'clock_in' ? '▶ In' : '■ Out'}
                    </span>
                  </td>
                  <td style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>{formatTime(tc.punchedAt)}</td>
                  <td style={{ color: 'var(--t3)', fontSize: 12 }}>{formatDate(tc.punchedAt)}</td>
                  <td style={{ fontSize: 12 }}>
                    {isAnomal
                      ? <span style={{ color: 'var(--amber)', fontWeight: 700 }}>⚠ {formatDuration(tc.durationMinutes)}</span>
                      : formatDuration(tc.durationMinutes)
                    }
                  </td>
                  <td>
                    {tc.lat && tc.lng
                      ? <a href={`https://maps.google.com/?q=${tc.lat},${tc.lng}`} target="_blank" rel="noreferrer" style={{ color: 'var(--cyan)', fontSize: 12 }}>📍 Map</a>
                      : <span style={{ color: 'var(--t4)', fontSize: 12 }}>—</span>
                    }
                  </td>
                  <td>
                    {tc.syncedAt
                      ? <span style={{ color: 'var(--teal)', fontSize: 11 }}>✓</span>
                      : <span style={{ color: 'var(--amber)', fontSize: 11 }}>Pending</span>
                    }
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
